/*
This is the form for creating a new task in the DragNDropTasks page
Not to be confused with the TaskForm component which is used to create a new task in the TaskList page

Due date will be the next Friday when created
*/
import { useState } from "react"
import { DraggableCardTaskProps } from "../../../interfaces/draggable_types"
import { GenericInput, GenericTextArea, GenericSelect } from "../../GenericForm"

export default function CreateTaskForm({cards, setCards, setErrorMessage}: {cards: DraggableCardTaskProps[], setCards: (cards: DraggableCardTaskProps[]) => void, setErrorMessage: (errorMessage: string) => void}) {
    // Task id is only used here to make sure that dragging the task will show the correct card (see handleDragStart in useTaskDragNDrop)
    // The real task id will be generated by the backend

    const [customTask, setCustomTask] = useState<DraggableCardTaskProps>({
        id: '',
        title: '',
        description: '',
        category: 'Electrical',
        parent: null,
        selected_employee: null
    })

    const onTaskNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomTask({...customTask, title: e.target.value})
    }

    const onTaskCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCustomTask({...customTask, category: e.target.value})
    }

    const onTaskDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCustomTask({...customTask, description: e.target.value})
    }

    const onSubmit = () => {
        if (!checkForErrors(customTask, setErrorMessage, cards)) return

        // Generate a new temporary task id
        const newTaskId = crypto.randomUUID()
        customTask.id = newTaskId

        const newCards = [...cards, customTask]
        setCards(newCards)
    }

    return(
        <div className='flex flex-col p-2 pt-2 rounded-md bg-stone-50 dark:bg-stone-900'>

            <h2 className='mb-2 text-xl font-bold text-blue-500 dark:text-blue-200'>
                Create Custom Task:
            </h2>

            <div className='flex flex-col gap-2'>
                <GenericInput label='Task Name' type="text" name='title' onChange={onTaskNameChange} />

                <GenericTextArea label='Task Description' name='description' onChange={onTaskDescriptionChange} optional />
                <GenericSelect label='Task Category' name='category' value={customTask.category} options={['Electrical', 'Plumbing', 'Mechanical', 'Other']} onChange={onTaskCategoryChange} />

                <button className='p-2 text-white bg-blue-500 rounded-md' onClick={onSubmit}>Add Task</button>
            </div>

            <HelpButton />
        </div>
    )
}

function checkForErrors(customTask: DraggableCardTaskProps, setErrorMessage: (errorMessage: string) => void, cards: DraggableCardTaskProps[]): boolean {
    if (customTask.title.trim() === '') {
        setErrorMessage('Task name is required')
        return false
    } else if (customTask.category.trim() === '') {
        setErrorMessage('Task category is required')
        return false
    } else if (customTask.title.trim().length > 100) {
        setErrorMessage('Task name cannot be longer than 100 characters')
        return false
    } else if (customTask.description.trim().length > 1024) {
        // Arbitrary number, but it's a good limit
        // Descriptions should be short and concise
        setErrorMessage('Task description cannot be longer than 1024 characters')
        return false
    } else if (cards.some(card => card.title === customTask.title)) {
        setErrorMessage('Custom task name must be unique')
        return false
    }

    return true
}

function HelpButton() {
    const [showHelp, setShowHelp] = useState(false)

    return (
        <>
            <button className='p-2 mt-2 text-sm text-gray-500 bg-red-100 rounded-md hover:bg-red-200' onClick={() => setShowHelp(!showHelp)}>
                How to use <span className='font-bold'>Custom Tasks</span>?
            </button>
            {showHelp && (
                <div className='p-2 text-sm text-gray-500 border border-gray-300 rounded-md dark:border-gray-700 dark:text-gray-200'>
                    <p>1. Enter the task name, description (optional), and category</p>
                    <p>2. Click the "Add Task" button below. This will create a new task at the bottom of the list.</p>
                    <p>3. Drag the task to the employee you want to assign it to</p>

                    <br /> 

                    <p>Note:</p>
                    <ul>
                        <li>- Due date will automatically be set to Friday of the week when created (Can be changed later)</li>
                        <li>- When created on Thursday and Fridays, the due date will be the following Friday</li>
                        <li>- Any custom tasks that are unassigned after finishing will be <span className='font-bold'>lost</span></li>
                    </ul>
                </div>

            )}
        </>

    )
}

